(() => {
  try {
    // prevent duplicate boots (Theme Editor re-renders can re-execute scripts)
    if (window.__KPP_BOOTED__) return;
    window.__KPP_BOOTED__ = true;

    const inEditor = !!(window.Shopify && Shopify.designMode);

    const isMobile = () =>
      window.matchMedia && window.matchMedia("(max-width: 767px)").matches;

    const storageGet = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
    const storageSet = (k, v) => { try { localStorage.setItem(k, v); } catch {} };

    // visitor counter (audience targeting)
    const VISIT_KEY = "kpp_visit_count";
    const visitCount = (() => {
      const n0 = parseInt(storageGet(VISIT_KEY) || "0", 10);
      const n1 = Number.isFinite(n0) ? n0 + 1 : 1;
      storageSet(VISIT_KEY, String(n1));
      return n1;
    })();
    const isReturning = visitCount > 1;

    const parseYMD = (s) => {
      const str = String(s || "").trim();
      if (!str) return null;
      const m = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) {
        const d = new Date(str);
        return Number.isNaN(d.getTime()) ? null : d;
      }
      const y = parseInt(m[1], 10);
      const mo = parseInt(m[2], 10) - 1;
      const da = parseInt(m[3], 10);
      const d = new Date(y, mo, da, 0, 0, 0, 0);
      return Number.isNaN(d.getTime()) ? null : d;
    };

    const detectPageType = () => {
      const p = location.pathname;
      if (p === "/" || p.startsWith("/?")) return "index";
      if (p.startsWith("/products/")) return "product";
      if (p.startsWith("/collections/")) return "collection";
      if (p.startsWith("/pages/")) return "page";
      if (p.startsWith("/blogs/")) return p.split("/").length >= 4 ? "article" : "blog";
      if (p.startsWith("/cart")) return "cart";
      return "all";
    };

    const roots = () => Array.from(document.querySelectorAll("[data-kpp-root]"));

    // global one-time fetch wrapper (for add_to_cart detection)
    const installFetchHookOnce = (() => {
      let installed = false;
      return () => {
        if (installed) return;
        installed = true;

        if (!window.fetch) return;
        const origFetch = window.fetch.bind(window);

        window.fetch = async (...args) => {
          const res = await origFetch(...args);
          try {
            const url = String(args[0]?.url || args[0] || "");
            if (url.includes("/cart/add")) {
              window.dispatchEvent(new CustomEvent("kpp:add_to_cart"));
            }
          } catch {}
          return res;
        };
      };
    })();

    const initRoot = (root) => {
      if (!root || root.dataset.kppInit === "1") return;
      root.dataset.kppInit = "1";

      const overlay = root.querySelector("[data-kpp-overlay]");
      const slot = root.querySelector("[data-kpp-slot]");

      let activeNode = null;
      let activeCloseKey = null;

      const lock = () => document.documentElement.classList.add("kpp-lock");
      const unlock = () => document.documentElement.classList.remove("kpp-lock");

      const close = (markClose) => {
        if (activeNode) activeNode.remove();
        activeNode = null;

        if (overlay) {
          overlay.hidden = true;
          overlay.onclick = null;
        }

        unlock();

        if (markClose && activeCloseKey) storageSet(activeCloseKey, String(Date.now()));
        activeCloseKey = null;
      };

      const templates = () =>
        Array.from(root.querySelectorAll("template[data-kpp-template]"));

      const passesTargeting = (tpl) => {
        // device
        const mobOk = String(tpl.dataset.kppMobile) === "true";
        const deskOk = String(tpl.dataset.kppDesktop) === "true";
        const m = isMobile();
        if (m && !mobOk) return false;
        if (!m && !deskOk) return false;

        // audience
        const audience = String(tpl.dataset.kppAudience || "all").trim();
        if (audience === "new" && isReturning) return false;
        if (audience === "returning" && !isReturning) return false;

        // page
        const target = String(tpl.dataset.kppPage || "all").trim();
        const pt = detectPageType();
        if (target !== "all" && target !== pt) return false;

        // url contains
        const u = String(tpl.dataset.kppUrlContains || "").trim();
        if (u && !location.href.includes(u)) return false;

        // date window
        const start = parseYMD(tpl.dataset.kppStart);
        const end = parseYMD(tpl.dataset.kppEnd);
        const now = new Date();
        if (start && now < start) return false;
        if (end) {
          const end2 = new Date(end.getTime());
          end2.setHours(23, 59, 59, 999);
          if (now > end2) return false;
        }

        return true;
      };

      const passesFrequency = (tpl, storeKey) => {
        const showOnce = String(tpl.dataset.kppShowOnce) === "true";
        const cooldownDays = Math.max(0, parseInt(tpl.dataset.kppCooldownDays || "0", 10));
        const lastTs = parseInt(storageGet(storeKey) || "0", 10);

        if (showOnce && lastTs > 0) return false;

        if (!showOnce && cooldownDays > 0 && lastTs > 0) {
          const ms = cooldownDays * 24 * 60 * 60 * 1000;
          if ((Date.now() - lastTs) < ms) return false;
        }

        return true;
      };

      const openByTemplate = (tpl, opts = {}) => {
        if (!tpl) return false;

        const force = !!opts.force;

        const key = (tpl.dataset.kppKey || "popup").trim() || "popup";
        const storeKey = `kpp_closed_ts:${key}`;

        if (!force) {
          if (!passesTargeting(tpl)) return false;
          if (!passesFrequency(tpl, storeKey)) return false;
        }

        close(false);

        const node = tpl.content?.firstElementChild
          ? tpl.content.firstElementChild.cloneNode(true)
          : null;
        if (!node) return false;

        activeNode = node;
        activeCloseKey = storeKey;

        // overlay var is on popup node; overlay element is sibling => copy to root
        const overlayVal = node.style?.getPropertyValue("--kpp-overlay");
        if (overlayVal) root.style.setProperty("--kpp-overlay", overlayVal.trim());

        // ---- Layouts (includes your added variants)
        const variant = String(tpl.dataset.kppVariant || "modal").trim();
        const isToast = (variant === "toast") || variant.startsWith("toast-");
        const isBanner = variant.startsWith("banner-");
        const isModal = (variant === "modal") || variant.startsWith("modal-");
        const isDrawer = variant.startsWith("drawer-");

        const useOverlay = !!overlay && !isToast && !isBanner;
        if (overlay) overlay.hidden = !useOverlay;

        if (isModal || isDrawer) lock();
        else unlock();

        slot.appendChild(node);

        const closeBackdrop = String(tpl.dataset.kppCloseBackdrop) === "true";
        const closeEsc = String(tpl.dataset.kppCloseEsc) === "true";

        node.addEventListener("click", (e) => {
          const btn = e.target.closest("[data-kpp-close]");
          if (btn) {
            e.preventDefault();
            close(true);
          }
        });

        if (useOverlay && closeBackdrop) {
          overlay.onclick = () => close(true);
        }

        const onKey = (e) => {
          if (e.key === "Escape" && closeEsc) close(true);
        };
        document.addEventListener("keydown", onKey, { once: true });

        return true;
      };

      const openByBlockId = (blockId, opts = {}) => {
        const bid = String(blockId || "");
        if (!bid) return false;
        const tpl = templates().find(t => String(t.dataset.kppBlockId || "") === bid);
        return tpl ? openByTemplate(tpl, opts) : false;
      };

      const openFirst = (opts = {}) => {
        const tpls = templates();
        if (!tpls.length) return false;
        return openByTemplate(tpls[0], opts);
      };

      // ---- Triggers (storefront only)
      const fired = new Set(); // per-root per-template key

      const fireOnce = (tpl, fn) => {
        const key = String(tpl.dataset.kppKey || tpl.dataset.kppBlockId || "");
        const k = `fire:${key}`;
        if (fired.has(k)) return;
        if (fn()) fired.add(k);
      };

      const tryOpen = (tpl) => fireOnce(tpl, () => openByTemplate(tpl, { force: false }));

      const setupTriggers = () => {
        if (inEditor) return;

        installFetchHookOnce();

        templates().forEach((tpl) => {
          const trig = String(tpl.dataset.kppTrigger || "delay").trim();

          if (trig === "manual") return;

          if (trig === "delay") {
            const delayMs = Math.max(0, parseInt(tpl.dataset.kppDelayMs || "0", 10));
            window.setTimeout(() => tryOpen(tpl), delayMs);
            return;
          }

          if (trig === "exit") {
            let did = false;
            const onLeave = (e) => {
              if (did) return;
              if (isMobile()) return;
              if (activeNode) return;
              if (e.clientY <= 0) {
                did = true;
                document.removeEventListener("mouseout", onLeave);
                tryOpen(tpl);
              }
            };
            document.addEventListener("mouseout", onLeave);
            return;
          }

          if (trig === "scroll") {
            const scrollPct = Math.max(0, parseInt(tpl.dataset.kppScroll || "0", 10));
            let did = false;

            const onScroll = () => {
              if (did) return;
              if (activeNode) return;

              const h = document.documentElement;
              const total = Math.max(1, h.scrollHeight - h.clientHeight);
              const pct = Math.round((h.scrollTop / total) * 100);

              if (pct >= scrollPct) {
                did = true;
                window.removeEventListener("scroll", onScroll);
                tryOpen(tpl);
              }
            };

            window.addEventListener("scroll", onScroll, { passive: true });
            onScroll();
            return;
          }

          if (trig === "idle") {
            const idleSec = Math.max(5, parseInt(tpl.dataset.kppIdleSec || "30", 10));
            let timer = 0;

            const arm = () => {
              if (timer) clearTimeout(timer);
              timer = setTimeout(() => {
                if (!activeNode) tryOpen(tpl);
              }, idleSec * 1000);
            };

            const onAct = () => arm();
            ["mousemove", "keydown", "scroll", "click", "touchstart"].forEach((ev) =>
              window.addEventListener(ev, onAct, { passive: true })
            );

            arm();
            return;
          }

          if (trig === "element") {
            const sel = String(tpl.dataset.kppElementSelector || "").trim();
            if (!sel) return;

            let attempts = 0;
            const maxAttempts = 10;

            const attach = () => {
              const el = document.querySelector(sel);
              if (!el) {
                attempts += 1;
                if (attempts <= maxAttempts) setTimeout(attach, 500);
                return;
              }

              if (!("IntersectionObserver" in window)) {
                tryOpen(tpl);
                return;
              }

              const io = new IntersectionObserver((entries) => {
                for (const ent of entries) {
                  if (ent.isIntersecting) {
                    io.disconnect();
                    if (!activeNode) tryOpen(tpl);
                    break;
                  }
                }
              }, { threshold: 0.15 });

              io.observe(el);
            };

            attach();
            return;
          }

          if (trig === "utm") {
            const q = String(tpl.dataset.kppUtmContains || "").trim();
            if (!q) return;
            if (location.search.includes(q) || location.href.includes(q)) {
              setTimeout(() => tryOpen(tpl), 100);
            }
            return;
          }

          if (trig === "add_to_cart") {
            const onATC = () => { if (!activeNode) tryOpen(tpl); };

            // 1) custom event from fetch hook
            window.addEventListener("kpp:add_to_cart", onATC);

            // 2) submit fallback
            document.addEventListener("submit", (e) => {
              const form = e.target;
              if (!form || !(form instanceof HTMLFormElement)) return;
              const action = String(form.getAttribute("action") || "");
              if (action.includes("/cart/add")) {
                setTimeout(onATC, 350);
              }
            }, true);

            return;
          }

          if (trig === "cart_threshold") {
            const th = parseFloat(String(tpl.dataset.kppCartThreshold || "0").trim());
            if (!Number.isFinite(th) || th <= 0) return;

            let busy = false;
            const check = async () => {
              if (busy) return;
              busy = true;
              try {
                const res = await fetch("/cart.js", { credentials: "same-origin" });
                if (!res.ok) return;
                const cart = await res.json();
                const totalCents = Number(cart && cart.total_price) || 0;
                const thresholdCents = Math.round(th * 100);
                if (totalCents >= thresholdCents) {
                  if (!activeNode) tryOpen(tpl);
                }
              } catch {
              } finally {
                busy = false;
              }
            };

            setTimeout(check, 800);
            window.addEventListener("kpp:add_to_cart", () => setTimeout(check, 250));

            return;
          }
        });
      };

      setupTriggers();

      // expose
      root.__kpp = { close, openByBlockId, openFirst, openByTemplate };
    };

    const initAll = () => roots().forEach(initRoot);

    // click-to-open (manual trigger)
    document.addEventListener("click", (e) => {
      const el = e.target.closest("[data-kpp-open]");
      if (!el) return;
      const key = el.getAttribute("data-kpp-open");
      if (!key) return;

      initAll();
      for (const r of roots()) {
        const esc = (window.CSS && CSS.escape) ? CSS.escape(key) : key.replace(/["\\]/g, "\\$&");
        const tpl = r.querySelector(`template[data-kpp-template][data-kpp-key="${esc}"]`);
        if (tpl && r.__kpp) {
          r.__kpp.openByTemplate(tpl, { force: true });
          return;
        }
      }
    });

    // -------------------------
    // EDITOR: show ONLY when selected (and allow disabling)
    // -------------------------
    const installEditorControllerOnce = () => {
      if (!inEditor) return;
      if (window.__KPP_EDITOR_CONTROLLER_INSTALLED__) return;
      window.__KPP_EDITOR_CONTROLLER_INSTALLED__ = true;

      let selectedSectionId = null;
      let selectedBlockId = null;

      const closeAll = () => {
        initAll();
        for (const r of roots()) {
          if (r && r.__kpp) r.__kpp.close(false);
        }
      };

      const editorPreviewEnabled = (r) =>
        String(r?.dataset?.kppEditorPreview || "true") === "true";

      const findRootByBlockId = (bid) => {
        const id = String(bid || "");
        if (!id) return null;

        // Prefer anchor (you already render it in Liquid)
        const anchors = Array.from(document.querySelectorAll("[data-kpp-editor-anchor][data-kpp-block-id]"));
        const a = anchors.find(x => String(x.getAttribute("data-kpp-block-id") || "") === id);
        if (a) return a.closest("[data-kpp-root]");

        // Fallback: search template attributes in roots
        for (const r of roots()) {
          if (r.querySelector(`template[data-kpp-template][data-kpp-block-id="${id.replace(/"/g, '\\"')}"]`)) {
            return r;
          }
        }
        return null;
      };

      const pickForcedTemplate = (r) =>
        r.querySelector('template[data-kpp-template][data-kpp-editor-force-preview="true"]');

      const pickFirstEnabledTemplate = (r) => {
        const tpls = Array.from(r.querySelectorAll("template[data-kpp-template]"));
        return tpls.find(t => String(t.dataset.kppEnabled || "") === "true") || null;
      };

      const openInRoot = (r, tpl) => {
        if (!r || !r.__kpp || !tpl) return false;
        return r.__kpp.openByTemplate(tpl, { force: true });
      };

      let timer = 0;
      const schedule = (ms = 120) => {
        if (timer) clearTimeout(timer);
        timer = setTimeout(refresh, ms);
      };

      const refresh = () => {
        initAll();

        // If no selection => keep editor clean
        if (!selectedBlockId && !selectedSectionId) {
          closeAll();
          return;
        }

        // Block selected
        if (selectedBlockId) {
          const r = findRootByBlockId(selectedBlockId);
          if (!r || !r.__kpp) { closeAll(); return; }
          if (!editorPreviewEnabled(r)) { closeAll(); return; }

          // If the selected block is one of our popups, open it; else close
          const ok = r.__kpp.openByBlockId(selectedBlockId, { force: true });
          if (ok) return;

          // If block is not a popup block (unlikely), show forced/first-enabled only if our section is selected
          if (selectedSectionId && String(r.dataset.kppSectionId || "") === String(selectedSectionId)) {
            const ft = pickForcedTemplate(r);
            if (ft) { openInRoot(r, ft); return; }
            const et = pickFirstEnabledTemplate(r);
            if (et) { openInRoot(r, et); return; }
          }

          closeAll();
          return;
        }

        // Section selected (no block)
        if (selectedSectionId) {
          const r = roots().find(x => String(x.dataset.kppSectionId || "") === String(selectedSectionId));
          if (!r || !r.__kpp) { closeAll(); return; }
          if (!editorPreviewEnabled(r)) { closeAll(); return; }

          const ft = pickForcedTemplate(r);
          if (ft) { openInRoot(r, ft); return; }

          const et = pickFirstEnabledTemplate(r);
          if (et) { openInRoot(r, et); return; }

          // nothing enabled and no forced preview
          closeAll();
          return;
        }

        closeAll();
      };

      const onBlockSelect = (e) => {
        const bid = e?.detail?.blockId ? String(e.detail.blockId) : "";
        if (!bid) return;

        selectedBlockId = bid;

        const r = findRootByBlockId(bid);
        if (r) selectedSectionId = String(r.dataset.kppSectionId || "");
        else if (e?.detail?.sectionId) selectedSectionId = String(e.detail.sectionId || "");

        schedule(60);
      };

      const onBlockDeselect = () => {
        selectedBlockId = null;
        schedule(80);
      };

      const onSectionSelect = (e) => {
        const sid = e?.detail?.sectionId ? String(e.detail.sectionId) : "";
        selectedSectionId = sid || null;
        selectedBlockId = null;
        schedule(80);
      };

      const onSectionDeselect = () => {
        selectedSectionId = null;
        selectedBlockId = null;
        schedule(60);
      };

      // Shopify editor events
      document.addEventListener("shopify:block:select", onBlockSelect, true);
      window.addEventListener("shopify:block:select", onBlockSelect, true);

      document.addEventListener("shopify:block:deselect", onBlockDeselect, true);
      window.addEventListener("shopify:block:deselect", onBlockDeselect, true);

      document.addEventListener("shopify:section:select", onSectionSelect, true);
      window.addEventListener("shopify:section:select", onSectionSelect, true);

      document.addEventListener("shopify:section:deselect", onSectionDeselect, true);
      window.addEventListener("shopify:section:deselect", onSectionDeselect, true);

      ["shopify:section:load", "shopify:section:unload", "shopify:section:reorder"].forEach((ev) => {
        const h = () => schedule(160);
        document.addEventListener(ev, h, true);
        window.addEventListener(ev, h, true);
      });

      // Settings changes: re-render sometimes doesn't fire a clean event
      document.addEventListener("input", () => schedule(220), true);
      document.addEventListener("change", () => schedule(220), true);

      // initial: keep clean (no selection) => it will close all
      schedule(260);
    };

    // init
    initAll();
    installEditorControllerOnce();

  } catch (e) {
    // never kill preview
    try { console.warn("[KPP] error:", e); } catch {}
  }
})();
